import { cn } from "../../../lib/utils";
import React from "react";

export type StepStatus = "done" | "current" | "upcoming";

export type StepItem = {
    id: string | number;
    label: string;
    icon?: React.ReactNode;
    status?: StepStatus;
};

type StepperProps = {
    steps: StepItem[];
    activeIndex?: number;
    split?: React.ReactNode | React.ReactNode[];
    containerClassName?: string;
    className?: string;
    size?: "sm" | "md" | "lg";
};

const DEFAULT_SPLIT = (
    <div className="w-8 h-px bg-gray-300 mx-3 shrink-0" aria-hidden />
);

const sizeMap = {
    sm: {
        iconBox: "text-sm",
        label: "text-sm",
        gap: "gap-2",
        padding: "px-2 py-1",
    },
    md: {
        iconBox: "text-base",
        label: "text-sm",
        gap: "gap-3",
        padding: "px-3 py-1.5",
    },
    lg: {
        iconBox: "text-lg",
        label: "text-base",
        gap: "gap-4",
        padding: "px-4 py-2",
    },
} as const;

export const Stepper: React.FC<StepperProps> = ({
    steps,
    activeIndex = 0,
    split,
    containerClassName = "",
    className = "",
    size = "md",
}) => {
    const sizes = sizeMap[size];

    // normalize split to a function that returns a node for a given index
    const renderSplit = (index: number) => {
        if (Array.isArray(split)) {
            return split[index] ?? DEFAULT_SPLIT;
        }
        if (split == null) return DEFAULT_SPLIT;
        return split;
    };

    return (
        <nav
            aria-label="Progress"
            className={cn(`w-full overflow-x-auto`, containerClassName)}
        >
            <ol className={cn(`flex items-center whitespace-nowrap ${sizes.gap} py-3 px-2`, className)}>
                {steps.map((step, idx) => {
                    const derivedStatus =
                        step.status ??
                        (idx < activeIndex ? "done" : idx === activeIndex ? "current" : "upcoming");

                    const isCurrent = derivedStatus === "current";
                    const isDone = derivedStatus === "done";

                    return (
                        <React.Fragment key={step.id}>
                            <li
                                className="flex items-center"
                                aria-current={isCurrent ? "step" : undefined}
                            >
                                {/* icon + label container */}
                                <div
                                    className={`flex items-center rounded-2xl ${isCurrent ? "" : "opacity-50"} ${sizes.padding} ${sizes.gap} transition-shadow`}
                                >
                                    {step.icon && (
                                        <div
                                            className={`flex items-center justify-center rounded-full shrink-0 ${sizes.iconBox}`}
                                        >
                                            {step.icon}
                                        </div>
                                    )}

                                    <div className={`flex items-center ${sizes.label}`}>
                                        <span className={`ml-0 font-semibold ${isCurrent ? "text-gray-900" : "text-[#0A0A0A]"}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                </div>
                            </li>

                            {/* separator */}
                            {idx !== steps.length - 1 && (
                                <li className="flex items-center" aria-hidden>
                                    {renderSplit(idx)}
                                </li>
                            )}
                        </React.Fragment>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Stepper;
